import { createFileRoute } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect } from 'react';
import { z } from 'zod';

import { ActivityBoard } from '@apps/web/components/home/activity_board';
import {
    type ActivityDateRange,
    activityDateRangeFromSearch,
} from '@apps/web/components/home/activity_filter_state';
import {
    type ActivityPresentation,
    ActivityPresentationToggle,
} from '@apps/web/components/home/activity_presentation_toggle';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { EVENTS } from '@apps/web/lib/events';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { ActivityFeedRoute } from '@apps/web/routes/_app/(main)/activity_feed_route';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const activitySearchSchema = z.object({
    eventId: z.string().optional(),
    status: z
        .enum(['open', 'done', 'needs-attention', 'all-history'])
        .optional(),
    view: z.enum(['needs_attention', 'all']).optional(),
    filter: z.string().optional(),
    org: z.string().optional(),
    office: z.string().optional(),
    coordinator: z.string().optional(),
    tag: z.string().optional(),
    excludedTag: z.string().optional(),
    q: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
    presentation: z.enum(['feed', 'list', 'board']).optional(),
});

export const Route = createFileRoute('/_app/(main)/activity')({
    component: ActivityRoute,
    validateSearch: activitySearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Activity | Phoebe',
            },
        ],
    }),
});

function ActivityRoute() {
    const organizationId = useOrganizationId();
    const posthog = usePostHog();
    const navigate = Route.useNavigate();
    const {
        presentation: presentationParam,
        from: fromParam,
        to: toParam,
    } = Route.useSearch();
    const { data: organization, isLoading: isLoadingOrganization } =
        useOrganization(organizationId, {
            reason: 'activity-route',
        });
    const isRedesignEnabled = useFeatureFlag(
        'activity_feed_redesign_enabled',
        organization?.features,
    );
    const requestedPresentation: ActivityPresentation =
        presentationParam === 'board' ? 'board' : 'list';
    // The board shares the list view's `from`/`to` search params, so a
    // chosen date range survives toggling between presentations.
    const boardDateRange = activityDateRangeFromSearch(fromParam, toParam);
    const handleBoardDateRangeChange = useCallback(
        (nextRange: ActivityDateRange | null) => {
            void navigate({
                to: '/activity',
                search: (previous) => ({
                    ...previous,
                    from: nextRange?.from,
                    to: nextRange?.to,
                }),
                replace: true,
            });
        },
        [navigate],
    );
    const handlePresentationChange = useCallback(
        (nextPresentation: ActivityPresentation) => {
            if (nextPresentation !== requestedPresentation) {
                try {
                    posthog?.capture(
                        EVENTS.ACTIVITY_BOARD_PRESENTATION_SWITCHED,
                        {
                            organization_id: organizationId,
                            from_presentation: requestedPresentation,
                            to_presentation: nextPresentation,
                        },
                    );
                } catch {
                    // Analytics must never block the presentation toggle.
                }
            }
            void navigate({
                to: '/activity',
                search: (previous) => ({
                    ...previous,
                    presentation:
                        nextPresentation === 'board' ? 'board' : undefined,
                    ...(nextPresentation === 'board'
                        ? { eventId: undefined }
                        : {}),
                }),
                replace: true,
            });
        },
        [navigate, organizationId, posthog, requestedPresentation],
    );

    // Canonicalize the presentation param once the flag state is known:
    // `board` is the only meaningful value, so legacy `feed`/`list` values
    // (and `board` itself when the redesign is off) get stripped. Wait for
    // the organization to actually load before acting — before the
    // organization id resolves, the (disabled) query reports
    // isLoading=false while the flag still reads false, and stripping then
    // would drop a deep-linked ?presentation=board on every fresh load.
    useEffect(() => {
        if (organization === undefined) {
            return;
        }
        const shouldStrip = isRedesignEnabled
            ? presentationParam === 'feed' || presentationParam === 'list'
            : presentationParam === 'board';
        if (!shouldStrip) {
            return;
        }
        void navigate({
            to: '/activity',
            search: (previous) => ({
                ...previous,
                presentation: undefined,
            }),
            replace: true,
        });
    }, [isRedesignEnabled, navigate, organization, presentationParam]);

    if (!organizationId) {
        return null;
    }
    if (isLoadingOrganization) {
        return null;
    }

    if (!isRedesignEnabled) {
        return <ActivityFeedRoute organizationId={organizationId} />;
    }

    const presentationToggle = (
        <ActivityPresentationToggle
            value={requestedPresentation}
            onChange={handlePresentationChange}
        />
    );

    if (requestedPresentation === 'list') {
        return (
            <ActivityFeedRoute
                organizationId={organizationId}
                header={presentationToggle}
                phoebeHelpEnabled={true}
            />
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-2 p-2 md:p-4">
            {presentationToggle}
            <div className="min-h-0 flex-1">
                <ActivityBoard
                    organizationId={organizationId}
                    layout="board"
                    dateRange={boardDateRange}
                    onDateRangeChange={handleBoardDateRangeChange}
                />
            </div>
        </div>
    );
}
